<template>
  <ConditionsList
    v-if="!currentEditingCondition"
    :conditions="formDetail?.conditionals || []"
    @click:add="handleAdd"
    @edit="handleEdit"
    @delete="handleDelete"
  />

  <ConditionEditor
    v-else
    :current-condition="currentEditingCondition"
    :loading="loading"
    @back="handleBackToList"
    @save="(condition) => handleSaveCondition(condition)"
  />
</template>

<script setup lang="ts">
import { ref, inject } from "vue";
import type { Ref } from "vue";
import { useRoute } from "vue-router";
import { message } from "ant-design-vue";

import { formConditionsManager } from "@/app/entities/utils/forms/FormConditionsManager";
import { Condition, Form } from "@/@types/entities/forms";
import { formService } from "@/app/entities/services";
import { ResponseError } from "@/app/shared/utils/httpManager";

import ConditionsList from "@/app/entities/components/lists/ConditionsList.vue";
import ConditionEditor from "@/app/entities/components/sections/forms/ConditionEditor.vue";

const emit = defineEmits<{ (e: "update"): void }>();

const route = useRoute();

const formDetail = inject("formDetail") as Ref<Form | undefined>;
const currentEditingCondition = ref<string | undefined>(undefined);

// View change methods
const handleAdd = () => {
  currentEditingCondition.value = formConditionsManager.getElementId("condition");
};

const handleEdit = (id: string) => {
  currentEditingCondition.value = id;
};

const handleBackToList = () => {
  currentEditingCondition.value = undefined;
};

// Update conditions
const loading = ref(false);

const handleSaveCondition = async (condition: Condition) => {
  const conditions = formConditionsManager.buildConditionsArray(
    condition,
    formDetail.value?.conditionals
  );

  await saveChanges(conditions, "Se guardaron los cambios");
};

const handleDelete = async (id: string) => {
  if (!formDetail.value?.conditionals) return;

  const conditions = formConditionsManager.removeCondition(id, formDetail.value?.conditionals);

  await saveChanges(conditions, "Se eliminó la condición");
};

const saveChanges = async (conditions: Condition[], successText: string) => {
  loading.value = true;

  try {
    const body: Omit<Form, "id" | "form"> = {
      name: formDetail.value?.name || "",
      description: formDetail.value?.description || "",
      conditionals: conditions,
    };

    await formService.edit(Number(route.params.id), body);

    message.success(successText);
    handleBackToList();

    emit("update");
  } catch (error) {
    let errorMessage = "Ha ocurrido un error";

    if (error instanceof ResponseError) {
      console.error(error.data);
      errorMessage = error.errors;
    }

    message.error(errorMessage);
  }

  loading.value = false;
};
</script>

<style scoped lang="sass"></style>
